import React, { useMemo, useState } from "react";
import { Bar } from "@reactchartjs/react-chart.js";
import { Box, IconButton, Paper, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import { compose } from "recompose";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";

// --- Constants & Custom Annotation Logic ---

const WEEKS_PER_PAGE = 7;

// FIX: Ensure 'chart.getDatasetMeta' is called on the Chart.js instance.
const drawAnnotations = (chartInstance) => {
  // Access the underlying Chart.js instance.
  const chart = chartInstance.chart || chartInstance;

  if (!chart || typeof chart.getDatasetMeta !== "function") {
    console.error(
      "Chart instance or getDatasetMeta method not found for annotation.",
    );
    return;
  }

  const ctx = chart.ctx;
  const barMeta = chart.getDatasetMeta(0); // Metadata for the bar dataset (index 0)

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.fillStyle = "#1976D2"; // Blue text color
  ctx.font = "bold 12px Arial";

  const barData = chart.config.data.datasets[0].data;

  // Function to format value with two decimal places, no currency, no K/M abbreviation
  const formatValueRaw = (value) => {
    return value;
  };

  // Iterate through each bar point
  barMeta.data.forEach((point, index) => {
    const dataValue = barData[index];
    // Use the raw formatting function
    const label = formatValueRaw(dataValue);

    // point._model.y is the top of the bar in Chart.js v2
    ctx.fillText(label, point._model.x, point._model.y - 5);
  });
  ctx.restore();
};

const options = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    xAxes: [{ gridLines: { display: false }, maxBarThickness: 50 }],
    yAxes: [
      {
        id: "y-axis-1",
        type: "linear",
        position: "left",
        ticks: {
          beginAtZero: true,
          // Custom formatter for y-axis ticks - display value as is (no currency, no rounding/abbreviation)
          callback: function (value, index, values) {
            // Using Intl.NumberFormat for cleaner large number formatting (e.g., adding commas)
            // but not adding currency or abbreviation.
            return new Intl.NumberFormat().format(value);
          },
        },
        gridLines: { color: "#e0e0e0" },
      },
    ],
  },
  tooltips: {
    enabled: true,
    filter: function (tooltipItem, data) {
      return tooltipItem.datasetIndex === 0; // Show tooltip only for bar dataset
    },
    callbacks: {
      label: function (tooltipItem, data) {
        // Tooltip displays the raw value with two decimals
        const value =
          tooltipItem.yLabel ||
          parseFloat(tooltipItem.formattedValue.replace(/[^0-9.-]+/g, ""));
        return `Value: ${value}`;
      },
    },
  },
  animation: {
    duration: 0,
    onComplete: drawAnnotations,
  },
};

// --- Bar Graph View Component ---

function BarGraphView({ classes, data }) {
  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(0);
  // Ensure data is initialized before calculating totalPages
  const totalPages = Math.ceil((data?.length || 0) / WEEKS_PER_PAGE);

  // --- Data Transformation (useMemo) ---
  const chartData = useMemo(() => {
    const startIndex = currentPage * WEEKS_PER_PAGE;
    const endIndex = startIndex + WEEKS_PER_PAGE;
    const slice = Array.isArray(data) ? data.slice(startIndex, endIndex) : [];

    if (slice.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Map data slice to Chart.js format
    const labels = slice.map((item) => {
      const date = new Date(item.activityDate);
      if (isNaN(date)) return item.activityDate;
      return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
        .getDate()
        .toString()
        .padStart(2, "0")}`;
    });

    const values = slice.map((item) => item.totalValue);

    return {
      labels: labels,
      datasets: [
        {
          type: "line",
          label: "Total Value",
          data: values,
          fill: false,
          borderColor: "#1976D2",
          backgroundColor: "#1976D2",
          pointRadius: 6,
          yAxisID: "y-axis-1",
        },
        {
          type: "bar",
          label: "Total Value",
          borderColor: "#d28819ff",
          backgroundColor: "#d28819ff",
          data: values,
        },
      ],
    };
  }, [data, currentPage]);

  // --- Pagination Handlers ---
  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (!data || data.length === 0) {
    return <Typography color="textSecondary">No data available.</Typography>;
  }

  return (
    <>
      <IconButton
        onClick={handlePrevious}
        disabled={currentPage === 0}
        size="small"
      >
        <ArrowBackIosIcon style={{ fontSize: 16 }} />
      </IconButton>

      <Box className={classes.chartWrapper}>
        <Bar data={chartData} options={options} />
      </Box>

      <IconButton
        onClick={handleNext}
        disabled={currentPage >= totalPages - 1}
        size="small"
      >
        <ArrowForwardIosIcon style={{ fontSize: 16 }} />
      </IconButton>
    </>
  );
}

const styles = (theme) => ({
  chartWrapper: {
    flexGrow: 1,
    position: "relative",
    height: "300px",
  },
});

export default compose(withStyles(styles, { withTheme: true }))(BarGraphView);
